import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { WorkforceOperationsNavComponent } from './workforce-operations-nav.component';

interface TimesheetRow {
  staff_id: number;
  staff_name: string;
  staff_email: string;
  date: string;
  total_seconds: number;
  total_hours: number;
  paid_hours?: number;
  hourly_rate: number;
  job_count: number;
  jobs_started_count: number;
  jobs_completed_count: number;
  session_count: number;
  has_active_shift: boolean;
  requires_time_review?: boolean;
  time_review_reasons?: string[];
  first_started_at: string | null;
  last_ended_at: string | null;
  estimated_gross_pay: number;
  estimated_paid_pay: number;
  break_entitlement: {
    paid_rest_breaks: number;
    paid_rest_minutes: number;
    unpaid_meal_breaks: number;
    unpaid_meal_minutes: number;
    paid_seconds?: number;
    paid_hours: number;
    summary: string;
  };
  shifts: Array<{
    id: number;
    started_at: string | null;
    ended_at: string | null;
    total_hours: number;
    status: string;
    requires_time_review?: boolean;
    review_reasons?: string[];
  }>;
  jobs?: Array<{
    id: string;
    status: string;
    priority: string;
    type: string;
    activity_at: string | null;
  }>;
}

interface WeeklyTimesheetRow extends TimesheetRow {
  week_start: string;
  week_end: string;
  daily_rows: TimesheetRow[];
}

interface CalendarDay {
  date: string;
  dayNumber: number;
  inMonth: boolean;
  rows: TimesheetRow[];
  staffCount: number;
  sessionCount: number;
  jobCount: number;
  totalHours: number;
  paidHours: number;
  estimatedPay: number;
  firstStart: string | null;
  lastEnd: string | null;
  hasActiveShift: boolean;
}

interface ShiftEditRow {
  id: number | null;
  started_at: string;
  ended_at: string;
  original_status: string;
  deleted: boolean;
}

interface ShiftEditorDay {
  date: string;
  label: string;
  shifts: Array<{ row: ShiftEditRow; index: number }>;
}

@Component({
  selector: 'app-admin-timesheet',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkforceOperationsNavComponent],
  template: `
    <div class="min-h-full bg-[#eef3f8] p-4 md:p-5">
      <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
        <div class="min-w-0">
          <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Workforce Operations</p>
          <h1 class="text-xl font-bold text-slate-950 tracking-tight leading-tight">Timesheet Control</h1>
          <p class="text-slate-500 text-[11px]">Last completed week, Monday to Sunday, with worked hours, paid hours, active shifts, and break entitlements by worker.</p>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">Week start (Mon)</span>
            <input type="date" [(ngModel)]="filters.from" (ngModelChange)="setWeekFromDate($event)" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
          </label>
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">Week end (Sun)</span>
            <input type="date" [(ngModel)]="filters.to" (ngModelChange)="setWeekFromDate($event)" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
          </label>
          <label class="block min-w-[180px]">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">Staff</span>
            <select [(ngModel)]="filters.staff_id" (ngModelChange)="onStaffChange()" class="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
              <option value="">All staff</option>
              @for (worker of workers(); track worker.id) {
                <option [value]="worker.id">{{ worker.name }}</option>
              }
            </select>
          </label>
          <div class="flex items-end gap-1">
            <button type="button" (click)="changeWeek(-1)" class="w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]">
              <i class="ti ti-chevron-left text-sm"></i>
            </button>
            <button type="button" (click)="goToLastWeek()" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:text-[#1A6FA6]">Last week</button>
            <button type="button" (click)="changeWeek(1)" class="w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]">
              <i class="ti ti-chevron-right text-sm"></i>
            </button>
          </div>
          <button type="button" (click)="loadTimesheet()" [disabled]="loading()" class="h-9 px-4 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] disabled:opacity-60 flex items-center gap-2">
            <i class="ti" [class.ti-loader]="loading()" [class.animate-spin]="loading()" [class.ti-refresh]="!loading()"></i>
            Refresh
          </button>
        </div>
      </div>

      <app-workforce-operations-nav active="timesheet"></app-workforce-operations-nav>

      <div class="mb-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <p class="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1A6FA6]">Showing completed week</p>
          <p class="text-[12px] font-semibold text-slate-800 mt-0.5">{{ weekRangeLabel() }} · Monday to Sunday</p>
        </div>
        <span class="inline-flex w-fit items-center gap-1.5 rounded-md bg-white border border-sky-100 px-2.5 py-1 text-[10px] font-bold text-slate-600">
          <i class="ti ti-calendar-week text-[#1A6FA6]"></i>
          Current week is not shown by default
        </span>
      </div>

      <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div class="bg-white rounded-lg border border-[#E7EEF5] px-4 py-3">
          <p class="text-[10px] font-semibold text-slate-400">Staff</p>
          <p class="text-2xl font-semibold text-slate-950 mt-1">{{ weeklyTotals()?.staff_count || 0 }}</p>
        </div>
        <div class="bg-white rounded-lg border border-[#E7EEF5] px-4 py-3">
          <p class="text-[10px] font-semibold text-slate-400">Worked hours</p>
          <p class="text-2xl font-semibold text-slate-950 mt-1">{{ weeklyTotals()?.total_hours || 0 }}</p>
        </div>
        <div class="bg-white rounded-lg border border-[#E7EEF5] px-4 py-3">
          <p class="text-[10px] font-semibold text-slate-400">Paid hours</p>
          <p class="text-2xl font-semibold text-emerald-600 mt-1">{{ weeklyTotals()?.paid_hours || 0 }}</p>
        </div>
        <div class="bg-white rounded-lg border border-[#E7EEF5] px-4 py-3">
          <p class="text-[10px] font-semibold text-slate-400">Paid rest breaks</p>
          <p class="text-2xl font-semibold text-[#1A6FA6] mt-1">{{ weeklyTotals()?.paid_rest_breaks || 0 }}</p>
        </div>
        <div class="bg-white rounded-lg border border-[#E7EEF5] px-4 py-3">
          <p class="text-[10px] font-semibold text-slate-400">Unpaid meals</p>
          <p class="text-2xl font-semibold text-amber-600 mt-1">{{ weeklyTotals()?.unpaid_meal_breaks || 0 }}</p>
        </div>
      </div>

      @if (filters.staff_id) {
      <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden mb-4">
        <div class="px-4 py-3 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 class="text-[12px] font-bold text-slate-900">Worker calendar</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">Selected worker daily activity inside the weekly calculation window.</p>
          </div>
          <div class="flex items-center gap-2">
            <button type="button" (click)="openSelectedWorkerEditor()" class="h-8 px-3 rounded-md bg-[#0C3A5A] text-white text-[10px] font-bold hover:bg-[#082d46] flex items-center gap-1.5">
              <i class="ti ti-edit"></i>
              Edit week shifts
            </button>
            <button type="button" (click)="changeWeek(-1)" class="w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]">
              <i class="ti ti-chevron-left text-sm"></i>
            </button>
            <div class="min-w-[180px] text-center">
              <p class="text-[12px] font-bold text-slate-900">{{ weekRangeLabel() }}</p>
              <p class="text-[10px] text-slate-400">{{ selectedWorkerName() }}</p>
            </div>
            <button type="button" (click)="changeWeek(1)" class="w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-[#1A6FA6]">
              <i class="ti ti-chevron-right text-sm"></i>
            </button>
          </div>
        </div>
        <div class="grid grid-cols-7 border-b border-slate-100 bg-slate-50/70 text-center">
          @for (day of weekDays; track day) {
            <div class="px-2 py-2 text-[9px] font-bold uppercase tracking-wide text-slate-400">{{ day }}</div>
          }
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7">
          @for (day of calendarDays(); track day.date) {
            <button type="button" (click)="selectCalendarDay(day)" class="min-h-[148px] border-b border-r border-slate-100 p-3 text-left transition hover:bg-sky-50/40" [ngClass]="{
              'bg-slate-50/50 text-slate-400': !day.inMonth,
              'bg-white text-slate-900': day.inMonth && selectedCalendarDate() !== day.date,
              'bg-sky-50 ring-2 ring-inset ring-[#1A6FA6]': selectedCalendarDate() === day.date,
              'border-l-4 border-l-emerald-500': day.hasActiveShift
            }">
              <div class="flex items-start justify-between gap-2">
                <span class="text-[12px] font-bold">{{ day.dayNumber }}</span>
                @if (day.hasActiveShift) {
                  <span class="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold">Live</span>
                }
              </div>
              @if (day.rows.length > 0) {
                <div class="mt-2 space-y-1">
                  <p class="text-[10px] font-semibold text-slate-600 truncate">{{ formatTime(day.firstStart) }} - {{ day.hasActiveShift ? 'Running' : formatTime(day.lastEnd) }}</p>
                  <div class="grid grid-cols-2 gap-1">
                    <span class="rounded bg-slate-100 px-1.5 py-1 text-[10px] font-semibold text-slate-700">{{ day.sessionCount }} shift{{ day.sessionCount === 1 ? '' : 's' }}</span>
                    <span class="rounded bg-blue-50 px-1.5 py-1 text-[10px] font-semibold text-[#1A6FA6]">{{ day.jobCount }} job{{ day.jobCount === 1 ? '' : 's' }}</span>
                    <span class="rounded bg-emerald-50 px-1.5 py-1 text-[10px] font-semibold text-emerald-700">{{ day.paidHours | number:'1.1-1' }} paid h</span>
                    <span class="rounded bg-amber-50 px-1.5 py-1 text-[10px] font-semibold text-amber-700">{{ day.estimatedPay | currency:'NZD':'symbol':'1.0-0' }}</span>
                  </div>
                </div>
              } @else {
                <p class="mt-8 text-[10px] text-slate-300">No activity</p>
              }
            </button>
          }
        </div>
        @if (selectedDay()) {
          <div class="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
            <div class="flex items-center justify-between gap-2 mb-2">
              <div>
                <p class="text-[11px] font-bold text-slate-900">{{ formatDate(selectedDay()!.date) }}</p>
                <p class="text-[10px] text-slate-500">{{ selectedDay()!.jobCount }} job{{ selectedDay()!.jobCount === 1 ? '' : 's' }}, {{ selectedDay()!.totalHours | number:'1.2-2' }} worked hours, {{ selectedDay()!.estimatedPay | currency:'NZD':'symbol':'1.2-2' }} estimated paid wage</p>
              </div>
              <button type="button" (click)="clearSelectedCalendarDay()" class="text-[10px] font-bold text-slate-400 hover:text-slate-700">Clear</button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              @for (row of selectedDay()!.rows; track rowKey(row)) {
                <div class="rounded-md border border-slate-200 bg-white px-3 py-2">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-[11px] font-bold text-slate-900 truncate">{{ row.staff_name }}</p>
                      <p class="text-[10px] text-slate-500">{{ formatTime(row.first_started_at) }} - {{ row.has_active_shift ? 'Running' : formatTime(row.last_ended_at) }}</p>
                    </div>
                    <span class="px-2 py-0.5 rounded-full bg-blue-50 text-[#1A6FA6] text-[10px] font-bold">{{ row.job_count || 0 }} jobs</span>
                  </div>
                  <div class="mt-2 grid grid-cols-3 gap-1 text-center">
                    <div class="rounded bg-slate-50 px-2 py-1">
                      <p class="text-[9px] text-slate-400">Worked</p>
                      <p class="text-[11px] font-bold text-slate-900">{{ row.total_hours | number:'1.2-2' }}</p>
                    </div>
                    <div class="rounded bg-emerald-50 px-2 py-1">
                      <p class="text-[9px] text-emerald-500">Paid</p>
                      <p class="text-[11px] font-bold text-emerald-700">{{ row.break_entitlement.paid_hours | number:'1.2-2' }}</p>
                    </div>
                    <div class="rounded bg-amber-50 px-2 py-1">
                      <p class="text-[9px] text-amber-500">Wage</p>
                      <p class="text-[11px] font-bold text-amber-700">{{ row.estimated_paid_pay | currency:'NZD':'symbol':'1.0-0' }}</p>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </section>
      }

      <div class="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <section class="xl:col-span-9 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Shift calculations</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Grouped by staff member for the selected week.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-sky-50 text-[#1A6FA6] text-[10px] font-bold">{{ weeklyRows().length }} weekly row{{ weeklyRows().length === 1 ? '' : 's' }}</span>
          </div>

          @if (loading()) {
            <div class="p-10 text-center text-slate-500 text-sm">
              <i class="ti ti-loader animate-spin text-xl"></i>
              <p class="mt-2">Loading timesheet...</p>
            </div>
          } @else if (weeklyRows().length === 0) {
            <div class="p-10 text-center">
              <div class="w-12 h-12 rounded-lg bg-slate-50 border border-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <i class="ti ti-clock-off text-2xl"></i>
              </div>
              <p class="text-sm font-semibold text-slate-900">No shifts found</p>
              <p class="text-xs text-slate-500 mt-1">Try a wider date range or another staff filter.</p>
            </div>
          } @else {
            <div class="overflow-x-auto">
              <table class="w-full min-w-[980px] text-left text-[11px]">
                <thead>
                  <tr class="border-b border-slate-100 bg-slate-50/70">
                    <th class="px-4 py-3 text-[9px] font-semibold text-slate-400">Staff</th>
                    <th class="px-3 py-3 text-[9px] font-semibold text-slate-400">Week</th>
                    <th class="px-3 py-3 text-[9px] font-semibold text-slate-400">Shift window</th>
                    <th class="px-3 py-3 text-[9px] font-semibold text-slate-400 text-right">Worked</th>
                    <th class="px-3 py-3 text-[9px] font-semibold text-slate-400 text-right">Paid hrs</th>
                    <th class="px-3 py-3 text-[9px] font-semibold text-slate-400">Break entitlement</th>
                    <th class="px-4 py-3 text-[9px] font-semibold text-slate-400 text-right">Pay estimate</th>
                    <th class="px-4 py-3 text-[9px] font-semibold text-slate-400 text-right">Correction</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of weeklyRows(); track rowKey(row)) {
                    <tr class="hover:bg-slate-50 align-top">
                      <td class="px-4 py-3">
                        <div class="flex items-start gap-2">
                          <button type="button" (click)="toggleExpanded(row)" class="mt-0.5 w-6 h-6 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-[#1A6FA6]">
                            <i class="ti text-[13px]" [class.ti-chevron-down]="expandedKey() === rowKey(row)" [class.ti-chevron-right]="expandedKey() !== rowKey(row)"></i>
                          </button>
                          <div class="min-w-0">
                            <p class="font-bold text-slate-900 truncate">{{ row.staff_name }}</p>
                            <p class="text-[10px] text-slate-400 truncate">{{ row.staff_email }}</p>
                            @if (row.has_active_shift) {
                              <span class="inline-flex mt-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">Active shift</span>
                            }
                            @if (row.requires_time_review) {
                              <span class="inline-flex mt-1 ml-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">Needs time review</span>
                            }
                          </div>
                        </div>
                      </td>
                      <td class="px-3 py-3 font-semibold text-slate-700">
                        <p>{{ formatDate(row.week_start) }}</p>
                        <p class="text-[10px] text-slate-400 mt-0.5">to {{ formatDate(row.week_end) }}</p>
                      </td>
                      <td class="px-3 py-3 text-slate-600">
                        <p>{{ formatDateTime(row.first_started_at) }} - {{ row.has_active_shift ? 'Running' : formatDateTime(row.last_ended_at) }}</p>
                        <p class="text-[10px] text-slate-400 mt-0.5">{{ row.session_count }} session{{ row.session_count === 1 ? '' : 's' }}</p>
                      </td>
                      <td class="px-3 py-3 text-right font-bold text-slate-900">{{ row.total_hours | number:'1.2-2' }}</td>
                      <td class="px-3 py-3 text-right font-bold text-emerald-700">{{ row.break_entitlement.paid_hours | number:'1.2-2' }}</td>
                      <td class="px-3 py-3">
                        <p class="font-semibold text-slate-800">{{ row.break_entitlement.summary }}</p>
                        <p class="text-[10px] text-slate-400 mt-0.5">{{ row.break_entitlement.paid_rest_minutes }} paid rest min, {{ row.break_entitlement.unpaid_meal_minutes }} unpaid meal min</p>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <p class="font-bold text-slate-900">{{ row.estimated_paid_pay | currency:'NZD':'symbol':'1.2-2' }}</p>
                        <p class="text-[10px] text-slate-400">gross {{ row.estimated_gross_pay | currency:'NZD':'symbol':'1.2-2' }}</p>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <button type="button" (click)="openShiftEditor(row)" class="h-8 px-3 rounded-md border border-sky-200 bg-sky-50 text-[#1A6FA6] text-[10px] font-bold hover:bg-sky-100 inline-flex items-center gap-1.5">
                          <i class="ti ti-edit"></i>
                          Edit week
                        </button>
                      </td>
                    </tr>
                    @if (expandedKey() === rowKey(row)) {
                      <tr class="bg-slate-50/70">
                        <td colspan="8" class="px-4 py-3">
                          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
                            @for (dayRow of row.daily_rows; track dayRow.staff_id + '-' + dayRow.date) {
                              <div class="rounded-md border border-slate-200 bg-white px-3 py-2">
                                <p class="text-[10px] font-bold text-slate-400">{{ formatDate(dayRow.date) }}</p>
                                <p class="text-[12px] font-semibold text-slate-900 mt-1">{{ formatDateTime(dayRow.first_started_at) }} - {{ dayRow.has_active_shift ? 'Running' : formatDateTime(dayRow.last_ended_at) }}</p>
                                <p class="text-[10px] text-slate-500 mt-1">{{ dayRow.total_hours | number:'1.2-2' }} worked, {{ dayRow.break_entitlement.paid_hours | number:'1.2-2' }} paid hours</p>
                                @if (dayRow.requires_time_review) {
                                  <p class="text-[10px] font-semibold text-amber-700 mt-1">{{ dayRow.time_review_reasons?.join('. ') }}. Correct this session before payroll.</p>
                                }
                              </div>
                            }
                            @for (shift of row.shifts; track shift.id) {
                              <div class="rounded-md border border-slate-200 bg-white px-3 py-2">
                                <div class="flex items-center justify-between gap-2">
                                  <p class="text-[10px] font-bold text-slate-400">Session #{{ shift.id }}</p>
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-semibold" [ngClass]="shift.requires_time_review ? 'bg-amber-100 text-amber-800' : (shift.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600')">{{ shift.requires_time_review ? 'Needs review' : shift.status }}</span>
                                </div>
                                <p class="text-[12px] font-semibold text-slate-900 mt-1">{{ formatDateTime(shift.started_at) }} - {{ shift.status === 'active' ? 'Running' : formatDateTime(shift.ended_at) }}</p>
                                <p class="text-[10px] text-slate-500 mt-1">{{ shift.total_hours | number:'1.2-2' }} hours</p>
                                @if (shift.requires_time_review) {
                                  <p class="text-[10px] font-semibold text-amber-700 mt-1">{{ shift.review_reasons?.join('. ') }}.</p>
                                }
                              </div>
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          }
        </section>

        <aside class="xl:col-span-3 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden h-fit">
          <div class="px-4 py-3 border-b border-slate-100">
            <h2 class="text-[12px] font-bold text-slate-900">Minimum breaks</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">Based on NZ shift length guidance.</p>
          </div>
          <div class="p-4 space-y-2">
            @for (rule of breakRules; track rule.hours) {
              <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                <p class="text-[11px] font-bold text-slate-900">{{ rule.hours }}</p>
                <p class="text-[10px] text-slate-500 mt-0.5">{{ rule.breaks }}</p>
              </div>
            }
          </div>
        </aside>
      </div>

      @if (shiftEditorOpen() && shiftEditorWorker()) {
        <div class="fixed inset-0 z-[90] bg-slate-950/50 p-3 md:p-6 flex items-center justify-center">
          <div class="w-full max-w-5xl max-h-[94vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col">
            <div class="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <p class="text-[9px] font-bold uppercase tracking-[0.14em] text-[#1A6FA6]">Admin shift correction</p>
                <h2 class="text-lg font-bold text-slate-950 mt-1">{{ shiftEditorWorker()!.name }}</h2>
                <p class="text-[11px] text-slate-500 mt-0.5">{{ weekRangeLabel() }} · Times shown in Auckland time</p>
              </div>
              <button type="button" (click)="closeShiftEditor()" [disabled]="shiftEditorSaving()" class="w-9 h-9 rounded-md border border-slate-200 text-slate-500 hover:text-slate-900 disabled:opacity-50">
                <i class="ti ti-x"></i>
              </button>
            </div>

            <div class="overflow-y-auto p-4 md:p-5 space-y-3">
              <div class="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 flex items-start gap-2">
                <i class="ti ti-info-circle text-amber-600 mt-0.5"></i>
                <p class="text-[11px] text-amber-800">Enter an end time to close a shift the worker forgot to stop. Saving recalculates worked and paid hours for the week.</p>
              </div>

              @for (day of shiftEditorDays(); track day.date) {
                <section class="rounded-lg border border-slate-200 overflow-hidden">
                  <div class="px-4 py-2.5 bg-slate-50 flex items-center justify-between gap-3">
                    <div>
                      <p class="text-[11px] font-bold text-slate-900">{{ day.label }}</p>
                      <p class="text-[9px] text-slate-400">{{ day.shifts.length }} shift{{ day.shifts.length === 1 ? '' : 's' }}</p>
                    </div>
                    <button type="button" (click)="addShiftForDay(day.date)" class="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-[10px] font-bold text-[#1A6FA6] hover:bg-sky-50 flex items-center gap-1">
                      <i class="ti ti-plus"></i>
                      Add shift
                    </button>
                  </div>
                  @if (day.shifts.length === 0) {
                    <div class="px-4 py-4 text-[11px] text-slate-400">No shift recorded for this day.</div>
                  } @else {
                    <div class="divide-y divide-slate-100">
                      @for (item of day.shifts; track item.row.id || item.row) {
                        <div class="p-4 grid grid-cols-1 md:grid-cols-[110px_1fr_1fr_auto] gap-3 md:items-end" [ngClass]="item.row.deleted ? 'bg-rose-50/70' : 'bg-white'">
                          <div>
                            <p class="text-[9px] font-semibold text-slate-400">Session</p>
                            <p class="text-[11px] font-bold text-slate-800 mt-1">{{ item.row.id ? '#' + item.row.id : 'New shift' }}</p>
                            @if (item.row.original_status === 'active') {
                              <span class="inline-flex mt-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-bold">Currently active</span>
                            }
                            @if (item.row.deleted) {
                              <span class="inline-flex mt-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold">Will be deleted</span>
                            }
                          </div>
                          <label class="block">
                            <span class="block text-[9px] font-semibold text-slate-500 mb-1">Start date and time</span>
                            <input type="datetime-local" [(ngModel)]="item.row.started_at" (ngModelChange)="refreshShiftEditorRows()" [disabled]="item.row.deleted" class="h-10 w-full rounded-md border border-slate-200 px-3 text-[12px] text-slate-800 outline-none focus:border-[#1A6FA6] disabled:bg-slate-100 disabled:text-slate-400">
                          </label>
                          <label class="block">
                            <span class="block text-[9px] font-semibold text-slate-500 mb-1">End date and time</span>
                            <input type="datetime-local" [(ngModel)]="item.row.ended_at" [disabled]="item.row.deleted" class="h-10 w-full rounded-md border border-slate-200 px-3 text-[12px] text-slate-800 outline-none focus:border-[#1A6FA6] disabled:bg-slate-100 disabled:text-slate-400">
                          </label>
                          <button type="button" (click)="toggleShiftDeletion(item.row)" class="h-10 w-10 rounded-md border" [ngClass]="item.row.deleted ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50' : 'border-rose-100 text-rose-500 hover:bg-rose-50'" [attr.aria-label]="item.row.deleted ? 'Undo shift deletion' : 'Delete shift'">
                            <i class="ti" [class.ti-restore]="item.row.deleted" [class.ti-trash]="!item.row.deleted"></i>
                          </button>
                        </div>
                      }
                    </div>
                  }
                </section>
              }

              <label class="block">
                <span class="block text-[10px] font-semibold text-slate-600 mb-1">Correction reason (optional)</span>
                <input type="text" [(ngModel)]="shiftEditorReason" maxlength="500" placeholder="Example: Worker forgot to stop shift" class="h-10 w-full rounded-md border border-slate-200 px-3 text-[12px] text-slate-800 outline-none focus:border-[#1A6FA6]">
              </label>
            </div>

            <div class="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button type="button" (click)="closeShiftEditor()" [disabled]="shiftEditorSaving()" class="h-10 px-4 rounded-md border border-slate-200 text-[11px] font-bold text-slate-600 disabled:opacity-50">Cancel</button>
              <button type="button" (click)="saveWeeklyShifts()" [disabled]="shiftEditorSaving()" class="h-10 px-5 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold disabled:opacity-60 flex items-center gap-2">
                <i class="ti" [class.ti-loader]="shiftEditorSaving()" [class.animate-spin]="shiftEditorSaving()" [class.ti-device-floppy]="!shiftEditorSaving()"></i>
                Save weekly shifts
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminTimesheetComponent implements OnInit {
  private api = inject(ApiService);
  loading = signal(false);
  rows = signal<TimesheetRow[]>([]);
  totals = signal<any>(null);
  workers = signal<any[]>([]);
  expandedKey = signal('');
  selectedCalendarDate = signal('');
  shiftEditorOpen = signal(false);
  shiftEditorSaving = signal(false);
  shiftEditorWorker = signal<{ id: number; name: string } | null>(null);
  shiftEditorRows = signal<ShiftEditRow[]>([]);
  shiftEditorReason = '';

  filters = {
    from: this.lastWeekStartIso(new Date()),
    to: this.lastWeekEndIso(new Date()),
    staff_id: ''
  };

  weeklyRows = computed(() => this.buildWeeklyRows());
  weeklyTotals = computed(() => this.buildWeeklyTotals());
  calendarDays = computed(() => this.buildCalendarDays());
  selectedDay = computed(() => this.calendarDays().find(day => day.date === this.selectedCalendarDate()) || null);
  shiftEditorDays = computed(() => this.buildShiftEditorDays());

  weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  breakRules = [
    { hours: '2-4 hours', breaks: '1 x 10-minute paid rest break' },
    { hours: 'More than 4-6 hours', breaks: '1 x 10-minute paid rest break + 1 x 30-minute unpaid meal break' },
    { hours: 'More than 6, less than 10 hours', breaks: '2 x 10-minute paid rest breaks + 1 x 30-minute unpaid meal break' },
    { hours: '10-12 hours', breaks: '3 x 10-minute paid rest breaks + 1 x 30-minute unpaid meal break' },
    { hours: 'More than 12-14 hours', breaks: '3 x 10-minute paid rest breaks + 2 x 30-minute unpaid meal breaks' }
  ];

  ngOnInit() {
    this.loadWorkers();
    this.loadTimesheet();
  }

  loadWorkers() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        this.workers.set(res.success && res.data?.users ? res.data.users : []);
      },
      error: () => this.workers.set([])
    });
  }

  loadTimesheet() {
    if (!this.filters.from || !this.filters.to) {
      showToast('error', 'Select a date range.');
      return;
    }
    this.normalizeToWeek(this.filters.from);
    if (this.filters.from > this.filters.to) {
      showToast('error', 'From date cannot be after to date.');
      return;
    }
    this.loading.set(true);
    this.api.getTimesheets(this.filters).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res.success && res.data) {
          this.rows.set(res.data.rows || []);
          this.totals.set(res.data.totals || null);
          this.expandedKey.set('');
          this.selectedCalendarDate.set('');
        } else {
          showToast('error', res.message || 'Timesheet could not be loaded.');
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        showToast('error', err?.error?.message || 'Timesheet could not be loaded.');
      }
    });
  }

  toggleExpanded(row: TimesheetRow) {
    const key = this.rowKey(row);
    this.expandedKey.set(this.expandedKey() === key ? '' : key);
  }

  rowKey(row: TimesheetRow): string {
    const weeklyRow = row as WeeklyTimesheetRow;
    return `${row.staff_id}-${weeklyRow.week_start || row.date}`;
  }

  changeWeek(direction: number) {
    const base = this.parseIsoDate(this.filters.from) || new Date();
    base.setDate(base.getDate() + (direction * 7));
    this.filters.from = this.weekStartIso(base);
    this.filters.to = this.weekEndIso(base);
    this.loadTimesheet();
  }

  goToLastWeek() {
    const today = new Date();
    this.filters.from = this.lastWeekStartIso(today);
    this.filters.to = this.lastWeekEndIso(today);
    this.loadTimesheet();
  }

  setWeekFromDate(value: string) {
    this.normalizeToWeek(value);
  }

  onStaffChange() {
    this.selectedCalendarDate.set('');
    this.loadTimesheet();
  }

  selectCalendarDay(day: CalendarDay) {
    if (day.rows.length === 0) {
      this.selectedCalendarDate.set('');
      return;
    }
    this.selectedCalendarDate.set(this.selectedCalendarDate() === day.date ? '' : day.date);
  }

  clearSelectedCalendarDay() {
    this.selectedCalendarDate.set('');
  }

  openShiftEditor(row: WeeklyTimesheetRow) {
    this.shiftEditorWorker.set({ id: Number(row.staff_id), name: row.staff_name });
    this.shiftEditorRows.set((row.shifts || [])
      .map(shift => ({
        id: Number(shift.id),
        started_at: this.toAucklandDateTimeInput(shift.started_at),
        ended_at: this.toAucklandDateTimeInput(shift.ended_at),
        original_status: shift.status || 'ended',
        deleted: false
      }))
      .sort((a, b) => a.started_at.localeCompare(b.started_at)));
    this.shiftEditorReason = '';
    this.shiftEditorOpen.set(true);
  }

  openSelectedWorkerEditor() {
    const staffId = Number(this.filters.staff_id || 0);
    if (!staffId) return;
    const row = this.weeklyRows().find(item => Number(item.staff_id) === staffId);
    if (row) {
      this.openShiftEditor(row);
      return;
    }
    this.shiftEditorWorker.set({ id: staffId, name: this.selectedWorkerName() });
    this.shiftEditorRows.set([]);
    this.shiftEditorReason = '';
    this.shiftEditorOpen.set(true);
  }

  closeShiftEditor() {
    if (this.shiftEditorSaving()) return;
    this.shiftEditorOpen.set(false);
    this.shiftEditorWorker.set(null);
    this.shiftEditorRows.set([]);
    this.shiftEditorReason = '';
  }

  addShiftForDay(date: string) {
    this.shiftEditorRows.update(rows => [
      ...rows,
      { id: null, started_at: `${date}T08:00`, ended_at: `${date}T17:00`, original_status: 'new', deleted: false }
    ].sort((a, b) => a.started_at.localeCompare(b.started_at)));
  }

  removeNewShift(row: ShiftEditRow) {
    if (row.id !== null) return;
    this.shiftEditorRows.update(rows => rows.filter(item => item !== row));
  }

  toggleShiftDeletion(row: ShiftEditRow) {
    if (row.id === null) {
      this.removeNewShift(row);
      return;
    }
    row.deleted = !row.deleted;
    this.refreshShiftEditorRows();
  }

  refreshShiftEditorRows() {
    this.shiftEditorRows.set([...this.shiftEditorRows()]);
  }

  saveWeeklyShifts() {
    const worker = this.shiftEditorWorker();
    if (!worker || this.shiftEditorSaving()) return;

    const shifts = this.shiftEditorRows();
    for (const shift of shifts.filter(item => !item.deleted)) {
      if (!shift.started_at) {
        showToast('error', 'Every shift requires a start time.');
        return;
      }
      if (shift.id === null && !shift.ended_at) {
        showToast('error', 'New shifts require an end time.');
        return;
      }
      if (shift.ended_at && new Date(shift.ended_at).getTime() <= new Date(shift.started_at).getTime()) {
        showToast('error', 'Shift end must be later than shift start.');
        return;
      }
    }

    this.shiftEditorSaving.set(true);
    this.api.updateWeeklyShifts({
      staff_id: worker.id,
      week_start: this.filters.from,
      week_end: this.filters.to,
      reason: this.shiftEditorReason.trim(),
      shifts: shifts.filter(shift => !shift.deleted).map(shift => ({
        id: shift.id,
        started_at: shift.started_at,
        ended_at: shift.ended_at
      })),
      deleted_shift_ids: shifts.filter(shift => shift.deleted && shift.id !== null).map(shift => Number(shift.id))
    }).subscribe({
      next: (res: any) => {
        this.shiftEditorSaving.set(false);
        if (!res.success) {
          showToast('error', res.message || 'Weekly shifts could not be updated.');
          return;
        }
        const updated = Number(res.data?.updated || 0);
        const created = Number(res.data?.created || 0);
        const deleted = Number(res.data?.deleted || 0);
        showToast('success', `${updated} shift${updated === 1 ? '' : 's'} updated${created ? `, ${created} added` : ''}${deleted ? `, ${deleted} deleted` : ''}.`);
        this.closeShiftEditor();
        this.loadTimesheet();
      },
      error: (err: any) => {
        this.shiftEditorSaving.set(false);
        showToast('error', err?.error?.message || 'Weekly shifts could not be updated.');
      }
    });
  }

  selectedWorkerName(): string {
    const selectedId = Number(this.filters.staff_id || 0);
    const worker = this.workers().find(item => Number(item.id) === selectedId);
    return worker?.name || 'Selected worker';
  }

  weekRangeLabel(): string {
    return `${this.formatDate(this.filters.from)} - ${this.formatDate(this.filters.to)}`;
  }

  private buildCalendarDays(): CalendarDay[] {
    const start = this.parseIsoDate(this.filters.from) || new Date();
    const end = this.parseIsoDate(this.filters.to) || new Date(start);

    const days: CalendarDay[] = [];
    const rowsByDate = new Map<string, TimesheetRow[]>();
    this.rows().forEach(row => {
      if (!rowsByDate.has(row.date)) {
        rowsByDate.set(row.date, []);
      }
      rowsByDate.get(row.date)!.push(row);
    });

    const dayCount = Math.max(1, Math.min(14, Math.round((end.getTime() - start.getTime()) / 86400000) + 1));
    for (let index = 0; index < dayCount; index += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const dateKey = this.localIsoDate(date);
      const rows = rowsByDate.get(dateKey) || [];
      const totalSeconds = rows.reduce((sum, row) => sum + Number(row.total_seconds || 0), 0);
      const paidHours = rows.reduce((sum, row) => sum + Number(row.break_entitlement?.paid_hours || 0), 0);
      const estimatedPay = rows.reduce((sum, row) => sum + Number(row.estimated_paid_pay || 0), 0);
      const firstStart = rows
        .map(row => row.first_started_at)
        .filter(Boolean)
        .sort()[0] || null;
      const lastEnd = rows
        .map(row => row.last_ended_at)
        .filter(Boolean)
        .sort()
        .reverse()[0] || null;

      days.push({
        date: dateKey,
        dayNumber: date.getDate(),
        inMonth: true,
        rows,
        staffCount: new Set(rows.map(row => row.staff_id)).size,
        sessionCount: rows.reduce((sum, row) => sum + Number(row.session_count || 0), 0),
        jobCount: rows.reduce((sum, row) => sum + Number(row.job_count || 0), 0),
        totalHours: Math.round((totalSeconds / 3600) * 100) / 100,
        paidHours: Math.round(paidHours * 100) / 100,
        estimatedPay: Math.round(estimatedPay * 100) / 100,
        firstStart,
        lastEnd,
        hasActiveShift: rows.some(row => row.has_active_shift)
      });
    }

    return days;
  }

  private buildShiftEditorDays(): ShiftEditorDay[] {
    const start = this.parseIsoDate(this.filters.from);
    if (!start) return [];

    const rows = this.shiftEditorRows();
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const dateKey = this.localIsoDate(date);
      return {
        date: dateKey,
        label: date.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' }),
        shifts: rows
          .map((row, rowIndex) => ({ row, index: rowIndex }))
          .filter(item => item.row.started_at.startsWith(dateKey))
      };
    });
  }

  private buildWeeklyRows(): WeeklyTimesheetRow[] {
    const groups = new Map<number, WeeklyTimesheetRow>();
    this.rows().forEach(row => {
      const staffId = Number(row.staff_id);
      const existing = groups.get(staffId);
      if (!existing) {
        groups.set(staffId, {
          ...row,
          date: this.filters.from,
          week_start: this.filters.from,
          week_end: this.filters.to,
          total_seconds: Number(row.total_seconds || 0),
          total_hours: 0,
          job_count: Number(row.job_count || 0),
          jobs_started_count: Number(row.jobs_started_count || 0),
          jobs_completed_count: Number(row.jobs_completed_count || 0),
          session_count: Number(row.session_count || 0),
          has_active_shift: !!row.has_active_shift,
          requires_time_review: !!row.requires_time_review,
          time_review_reasons: [...(row.time_review_reasons || [])],
          first_started_at: row.first_started_at,
          last_ended_at: row.last_ended_at,
          shifts: [...(row.shifts || [])],
          jobs: [...(row.jobs || [])],
          daily_rows: [row]
        });
        return;
      }

      existing.total_seconds += Number(row.total_seconds || 0);
      existing.job_count += Number(row.job_count || 0);
      existing.jobs_started_count += Number(row.jobs_started_count || 0);
      existing.jobs_completed_count += Number(row.jobs_completed_count || 0);
      existing.session_count += Number(row.session_count || 0);
      existing.has_active_shift = existing.has_active_shift || !!row.has_active_shift;
      existing.requires_time_review = existing.requires_time_review || !!row.requires_time_review;
      existing.time_review_reasons = Array.from(new Set([
        ...(existing.time_review_reasons || []),
        ...(row.time_review_reasons || [])
      ]));
      existing.shifts.push(...(row.shifts || []));
      existing.jobs?.push(...(row.jobs || []));
      existing.daily_rows.push(row);

      if (row.first_started_at && (!existing.first_started_at || row.first_started_at < existing.first_started_at)) {
        existing.first_started_at = row.first_started_at;
      }
      if (row.last_ended_at && (!existing.last_ended_at || row.last_ended_at > existing.last_ended_at)) {
        existing.last_ended_at = row.last_ended_at;
      }
    });

    return Array.from(groups.values()).map(row => {
      row.daily_rows.sort((a, b) => a.date.localeCompare(b.date));
      row.shifts.sort((a, b) => String(a.started_at || '').localeCompare(String(b.started_at || '')));
      row.total_hours = Math.round((row.total_seconds / 3600) * 100) / 100;
      row.break_entitlement = this.calculateBreakEntitlement(row.total_seconds);
      row.estimated_gross_pay = Math.round(row.total_hours * Number(row.hourly_rate || 0) * 100) / 100;
      row.estimated_paid_pay = Math.round(row.break_entitlement.paid_hours * Number(row.hourly_rate || 0) * 100) / 100;
      return row;
    }).sort((a, b) => a.staff_name.localeCompare(b.staff_name));
  }

  private buildWeeklyTotals(): any {
    const rows = this.weeklyRows();
    const staffSeen = new Set(rows.map(row => row.staff_id));
    const totalSeconds = rows.reduce((sum, row) => sum + Number(row.total_seconds || 0), 0);
    const paidSeconds = rows.reduce((sum, row) => sum + Number(row.break_entitlement?.paid_seconds || 0), 0);

    return {
      staff_count: staffSeen.size,
      row_count: rows.length,
      job_count: rows.reduce((sum, row) => sum + Number(row.job_count || 0), 0),
      total_seconds: totalSeconds,
      paid_seconds: paidSeconds,
      total_hours: Math.round((totalSeconds / 3600) * 100) / 100,
      paid_hours: Math.round((paidSeconds / 3600) * 100) / 100,
      paid_rest_breaks: rows.reduce((sum, row) => sum + Number(row.break_entitlement?.paid_rest_breaks || 0), 0),
      unpaid_meal_breaks: rows.reduce((sum, row) => sum + Number(row.break_entitlement?.unpaid_meal_breaks || 0), 0),
      active_shift_count: rows.filter(row => row.has_active_shift).length
    };
  }

  private calculateBreakEntitlement(workedSeconds: number): TimesheetRow['break_entitlement'] {
    const workedMinutes = Math.max(0, Math.ceil(Number(workedSeconds || 0) / 60));
    const blockMinutes = 8 * 60;
    const fullBlocks = Math.floor(workedMinutes / blockMinutes);
    const remainder = workedMinutes % blockMinutes;

    let paidRestBreaks = fullBlocks * 2;
    let unpaidMealBreaks = fullBlocks;

    if (remainder >= 2 * 60 && remainder <= 4 * 60) {
      paidRestBreaks += 1;
    } else if (remainder > 4 * 60 && remainder <= 6 * 60) {
      paidRestBreaks += 1;
      unpaidMealBreaks += 1;
    } else if (remainder > 6 * 60) {
      paidRestBreaks += 2;
      unpaidMealBreaks += 1;
    }

    const paidRestMinutes = paidRestBreaks * 10;
    const unpaidMealMinutes = unpaidMealBreaks * 30;
    const paidSeconds = Math.max(0, Number(workedSeconds || 0) - (unpaidMealMinutes * 60));

    return {
      paid_rest_breaks: paidRestBreaks,
      paid_rest_minutes: paidRestMinutes,
      unpaid_meal_breaks: unpaidMealBreaks,
      unpaid_meal_minutes: unpaidMealMinutes,
      paid_seconds: paidSeconds,
      paid_hours: Math.round((paidSeconds / 3600) * 100) / 100,
      summary: this.breakSummary(paidRestBreaks, unpaidMealBreaks)
    };
  }

  private breakSummary(restBreaks: number, mealBreaks: number): string {
    if (restBreaks === 0 && mealBreaks === 0) {
      return 'No minimum break entitlement';
    }

    const parts: string[] = [];
    if (restBreaks > 0) {
      parts.push(`${restBreaks} x 10-minute paid rest break${restBreaks === 1 ? '' : 's'}`);
    }
    if (mealBreaks > 0) {
      parts.push(`${mealBreaks} x 30-minute unpaid meal break${mealBreaks === 1 ? '' : 's'}`);
    }
    return parts.join(' + ');
  }

  formatDate(value: string | null): string {
    if (!value) return '--';
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  formatTime(value: string | null): string {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleTimeString('en-NZ', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Pacific/Auckland'
    });
  }

  formatDateTime(value: string | null): string {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Pacific/Auckland',
      timeZoneName: 'short'
    });
  }

  private toAucklandDateTimeInput(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value || '';
    return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
  }

  private weekStartIso(date: Date): string {
    const start = new Date(date);
    const weekday = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - weekday);
    return this.localIsoDate(start);
  }

  private weekEndIso(date: Date): string {
    const end = this.parseIsoDate(this.weekStartIso(date)) || new Date(date);
    end.setDate(end.getDate() + 6);
    return this.localIsoDate(end);
  }

  private lastWeekStartIso(date: Date): string {
    const start = this.parseIsoDate(this.weekStartIso(date)) || new Date(date);
    start.setDate(start.getDate() - 7);
    return this.localIsoDate(start);
  }

  private lastWeekEndIso(date: Date): string {
    const end = this.parseIsoDate(this.lastWeekStartIso(date)) || new Date(date);
    end.setDate(end.getDate() + 6);
    return this.localIsoDate(end);
  }

  private normalizeToWeek(value: string) {
    const anchor = this.parseIsoDate(value) || this.parseIsoDate(this.filters.from) || new Date();
    this.filters.from = this.weekStartIso(anchor);
    this.filters.to = this.weekEndIso(anchor);
  }

  private parseIsoDate(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private localIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
